import { LocalDate } from '@js-joda/core'
import {
    DataQualityAggregatedTimeseries,
    DataQualityOverallScores,
    GetBusinessEstimateAnalyticsResponse,
    ShippingEmissionTimeseries,
    WtwBreakdownTimeseries,
} from '@lune-fe/dapi-models'
import {
    EmissionBreakdownChartToggleNew,
    truncateDateToLocalDate,
} from '@lune-fe/lune-components-lib'
import { ButtonGroupNew, LoadingWrapper, MainLayoutContainerNew } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { memo, RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { Range } from 'react-date-range'

import { PredefinedRange } from 'components/DateRangeFilterNew'
import FiltersBoxNew from 'components/FiltersBoxNew'
import FiltersWithQueryParamsNew from 'components/FiltersWithQueryParamsNew'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import {
    getAccountsById,
    getBusinessEstimatesAnalytics,
    getCumulativeAnalyticsPerBundle,
    getDataQualityAggregatedTimeseries,
    getOverallDataHealthScores,
    getShippingEmissionTimeseries,
} from 'endpoints/dapi'
import useIsTestMode from 'hooks/useIsTestMode'
import useMixpanel from 'hooks/useMixpanel'
import useRecentlyUsedAccounts from 'hooks/useRecentlyUsedAccounts'
import useTitle from 'hooks/useTitle'
import { OrderType } from 'models/order'
import { SnackbarMessages } from 'SnackbarMessages'
import ActiveFiltersChips from 'views/CalculateEmissions/Tables/ActiveFiltersChips'
import BusinessEmissionsSections from 'views/Dashboard/BusinessEmissionsSections'
import CarbonIntensityTrend from 'views/Dashboard/CarbonIntensityTrend'
import { BundleData } from 'views/Dashboard/Charts/OrdersChartConfig'
import EmissionsByTransportMode from 'views/Dashboard/EmissionsByTransportMode'
import EmptyOrdersSection from 'views/Dashboard/EmptyOrdersSection'
import OrdersAnalytics from 'views/Dashboard/OrdersAnalytics'
import WtwBreakdown from 'views/Dashboard/WtwBreakdown'
import DataHealthScore from 'views/Homepage/DataHealthScore'

import EmptyAnalyticsSection from './EmptyAnalyticsSection'

const compareParameters = (prevProps: any, nextProps: any): boolean => {
    const { dateRange, selectedAccountIds, analyticsType } = prevProps
    const {
        dateRange: nextDateRange,
        selectedAccountIds: nextSelectedAccountIds,
        analyticsType: nextAnalyticsType,
    } = nextProps

    // Return true if props are equal (skip re-render), false if different (re-render)
    return (
        JSON.stringify(dateRange) === JSON.stringify(nextDateRange) &&
        JSON.stringify(selectedAccountIds) === JSON.stringify(nextSelectedAccountIds) &&
        analyticsType === nextAnalyticsType
    )
}

const OrderChartsWrappers = ({ dateRange, data }: { dateRange: Range; data?: BundleData[] }) => {
    const [analyticsType, setAnalyticsType] = useState<OrderType>(OrderType.QUANTITY)
    return (
        <OrdersAnalytics
            orderChartData={data}
            setAnalyticsType={setAnalyticsType}
            dateRange={dateRange}
            analyticsType={analyticsType}
        />
    )
}

const getStartEndDates = (dateRange: Range) => {
    const start = dateRange.startDate
        ? truncateDateToLocalDate(dateRange.startDate)
        : dateRange.startDate
    const end = dateRange.endDate ? truncateDateToLocalDate(dateRange.endDate) : dateRange.endDate
    return { start, end }
}

const getInterval = (dateRange: Range): 'day' | 'seven_days' | 'month' => {
    if (!dateRange.endDate || !dateRange.startDate) {
        return 'month'
    }
    const rangeDuration = moment.duration(moment(dateRange.endDate).diff(dateRange.startDate))
    const days = Math.round(rangeDuration.asDays())
    if (days <= 31) {
        return 'day'
    } else if (days <= 90) {
        return 'seven_days'
    } else {
        return 'month'
    }
}

const ChartsWrapper = ({
    selectedAccountIds,
    dateRange,
    analyticsType,
}: {
    selectedAccountIds: string[]
    dateRange: Range
    analyticsType: 'Logistics' | 'Spend'
}) => {
    const { enqueueSnackbar: snackbar } = useSnackbar()

    const [loading, setLoading] = useState<boolean>(true)
    const [allData, setAllData] = useState<{
        shippingEmissionsTimeseriesData?: {
            wtwBreakdownTimeseries: WtwBreakdownTimeseries
            emissionTimeseries: ShippingEmissionTimeseries
            carbonIntensityTimeseries: Array<{
                date: string
                average: number
            }>
        }
        orderChartData?: BundleData[]
        overallScore?: DataQualityOverallScores
        aggregatedScoreData?: DataQualityAggregatedTimeseries
        businessAnalytics?: GetBusinessEstimateAnalyticsResponse
    }>({
        shippingEmissionsTimeseriesData: undefined,
        orderChartData: undefined,
        overallScore: undefined,
        aggregatedScoreData: undefined,
        businessAnalytics: undefined,
    })

    const from = useMemo(() => getStartEndDates(dateRange).start, [dateRange])
    const through = useMemo(() => getStartEndDates(dateRange).end, [dateRange])
    const interval = useMemo(() => getInterval(dateRange), [dateRange])

    const loadShippingEmissionTimeseriesData = useCallback(async () => {
        try {
            return await getShippingEmissionTimeseries({
                from,
                through,
                interval,
                accountIds: selectedAccountIds,
            })
        } catch {
            snackbar(SnackbarMessages.LOAD_ANALYTICS_FAIL)
        }
    }, [from, through, interval, selectedAccountIds, snackbar])

    const loadOrderChartData = useCallback(async () => {
        try {
            return await getCumulativeAnalyticsPerBundle({
                from,
                through,
                accountIds: selectedAccountIds,
            })
        } catch {
            snackbar(SnackbarMessages.LOAD_ANALYTICS_FAIL)
        }
    }, [from, selectedAccountIds, snackbar, through])

    const loadOverallDataQualityScore = useCallback(async () => {
        try {
            return await getOverallDataHealthScores(from, through, selectedAccountIds)
        } catch {
            snackbar(SnackbarMessages.LOAD_ANALYTICS_FAIL)
        }
    }, [from, snackbar, through, selectedAccountIds])

    const loadDataQualityScoreStats = useCallback(async () => {
        try {
            return await getDataQualityAggregatedTimeseries({
                from,
                through,
                accountIds: selectedAccountIds,
            })
        } catch {
            snackbar(SnackbarMessages.LOAD_ANALYTICS_FAIL)
        }
    }, [from, selectedAccountIds, snackbar, through])

    const loadBusinessEstimatesAnalytics = useCallback(async () => {
        try {
            const fromDate = dateRange.startDate
                ? LocalDate.parse(moment(dateRange.startDate).format('YYYY-MM-DD'))
                : undefined
            const throughDate = dateRange.endDate
                ? LocalDate.parse(moment(dateRange.endDate).format('YYYY-MM-DD'))
                : undefined

            if (!fromDate || !throughDate) {
                return undefined
            }

            const result = await getBusinessEstimatesAnalytics({
                from: fromDate,
                through: throughDate,
                accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
            })

            if (result.isOk()) {
                return result.value
            } else {
                snackbar(SnackbarMessages.GENERIC_ERROR)
                return undefined
            }
        } catch {
            snackbar(SnackbarMessages.LOAD_ANALYTICS_FAIL)
            return undefined
        }
    }, [dateRange, selectedAccountIds, snackbar])

    useEffect(() => {
        setLoading(true)
        // load all data
        Promise.all([
            loadShippingEmissionTimeseriesData(),
            loadOrderChartData(),
            loadOverallDataQualityScore(),
            loadDataQualityScoreStats(),
            loadBusinessEstimatesAnalytics(),
        ])
            .then(
                ([
                    shippingEmissionsTimeseriesData,
                    orderChartData,
                    overallScore,
                    aggregatedScoreData,
                    businessEstimateAnalytics,
                ]) => {
                    setAllData((prev) => ({
                        ...prev,
                        shippingEmissionsTimeseriesData,
                        orderChartData,
                        overallScore,
                        aggregatedScoreData,
                        businessAnalytics: businessEstimateAnalytics,
                    }))
                    setLoading(false)
                },
            )
            .catch(() => ({
                shippingEmissionsTimeseriesData: undefined,
                orderChartData: undefined,
                scores: undefined,
                aggregatedData: undefined,
                businessAnalytics: undefined,
            }))
    }, [
        loadOrderChartData,
        loadShippingEmissionTimeseriesData,
        loadOverallDataQualityScore,
        loadDataQualityScoreStats,
        snackbar,
    ])

    // Determine if Logistics analytics is empty
    const isLogisticsAnalyticsEmpty = useMemo(() => {
        const data = allData.shippingEmissionsTimeseriesData
        if (!data || data.emissionTimeseries.length === 0) {
            return true
        }

        // Check if all emissions are zero across all timeseries
        const hasAnyEmissions = data.emissionTimeseries.some((day) => {
            return (
                (day.sea || 0) +
                    (day.air || 0) +
                    (day.road || 0) +
                    (day.rail || 0) +
                    (day.inlandWaterway || 0) +
                    (day.logisticSite || 0) >
                0
            )
        })

        return !hasAnyEmissions
    }, [allData.shippingEmissionsTimeseriesData])

    // Determine if Business analytics is empty
    const isBusinessAnalyticsEmpty = useMemo(() => {
        const data = allData.businessAnalytics
        if (!data?.timeseries || data.timeseries.length === 0) {
            return true
        }

        // Check if total emissions are zero by summing all category values
        const totalEmissions = data.timeseries.reduce((sum, day) => {
            // Sum all numeric properties except 'date'
            const dayTotal = Object.entries(day).reduce((daySum, [key, value]) => {
                if (key === 'date' || typeof value !== 'number') {
                    return daySum
                }
                return daySum + value
            }, 0)
            return sum + dayTotal
        }, 0)

        return totalEmissions === 0
    }, [allData.businessAnalytics])

    // Aggregate wtwBreakdownTimeseries into totals
    const timeseries = allData.shippingEmissionsTimeseriesData?.wtwBreakdownTimeseries
    let wtwBreakdown: { wtt: string; ttw: string; unknown: string } | undefined
    if (timeseries && timeseries.length > 0) {
        const totals = timeseries.reduce(
            (acc, dataPoint) => ({
                wtt: acc.wtt + dataPoint.wtt,
                ttw: acc.ttw + dataPoint.ttw,
                unknown: acc.unknown + dataPoint.unknown,
            }),
            { wtt: 0, ttw: 0, unknown: 0 },
        )

        wtwBreakdown = {
            wtt: totals.wtt.toString(),
            ttw: totals.ttw.toString(),
            unknown: totals.unknown.toString(),
        }
    }

    return loading ? (
        <WhiteBoxContainer>
            <LoadingWrapper
                sx={{
                    display: 'flex',
                    position: 'relative',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: `600px`,
                }}
                loading={loading}
            >
                <></>
            </LoadingWrapper>
        </WhiteBoxContainer>
    ) : (
        <>
            {analyticsType === 'Logistics' ? (
                <>
                    {isLogisticsAnalyticsEmpty ? (
                        <EmptyAnalyticsSection />
                    ) : (
                        <>
                            <WhiteBoxContainer
                                sx={{
                                    p: 3,
                                    mb: 3 / 2,
                                }}
                            >
                                <Stack
                                    direction="column"
                                    sx={{
                                        width: '100%',
                                    }}
                                    spacing={{
                                        xs: 3,
                                    }}
                                >
                                    <EmissionBreakdownChartToggleNew
                                        loading={loading}
                                        data={allData.shippingEmissionsTimeseriesData}
                                        dateRange={dateRange}
                                    />
                                </Stack>
                            </WhiteBoxContainer>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 3 / 2,
                                    mb: 3 / 2,
                                }}
                            >
                                <WhiteBoxContainer
                                    sx={{
                                        width: '50%',
                                        p: 3,
                                        height: '170px',
                                    }}
                                >
                                    <DataHealthScore
                                        vertical
                                        shippedOnly
                                        overallScores={allData.overallScore}
                                        aggregatedScoreData={allData.aggregatedScoreData}
                                    />
                                </WhiteBoxContainer>
                                <WhiteBoxContainer
                                    sx={{
                                        width: '50%',
                                        p: 3,
                                        height: '170px',
                                    }}
                                >
                                    <WtwBreakdown breakdown={wtwBreakdown} />
                                </WhiteBoxContainer>
                            </Box>
                            {allData.shippingEmissionsTimeseriesData && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 3 / 2,
                                        mb: 3 / 2,
                                        '> div': {
                                            flex: '1 1 auto',
                                            width: {
                                                xs: '100%',
                                                lg: '50%',
                                            },
                                        },
                                        flexDirection: {
                                            xs: 'column',
                                            lg: 'row',
                                        },
                                    }}
                                >
                                    <EmissionsByTransportMode
                                        emissionTimeseries={
                                            allData.shippingEmissionsTimeseriesData
                                                .emissionTimeseries
                                        }
                                    />
                                    <CarbonIntensityTrend
                                        loading={loading}
                                        dateRange={dateRange}
                                        carbonIntensityTimeseries={
                                            allData.shippingEmissionsTimeseriesData
                                                .carbonIntensityTimeseries
                                        }
                                        wtwBreakdownTimeseries={
                                            allData.shippingEmissionsTimeseriesData
                                                .wtwBreakdownTimeseries
                                        }
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            ) : (
                <>
                    {isBusinessAnalyticsEmpty ? (
                        <EmptyAnalyticsSection />
                    ) : (
                        <BusinessEmissionsSections
                            data={allData.businessAnalytics}
                            dateRange={dateRange}
                        />
                    )}
                </>
            )}
            {allData.orderChartData?.length ? (
                <WhiteBoxContainer
                    sx={{
                        p: 3,
                        mb: 3 / 2,
                    }}
                >
                    <Stack
                        direction="column"
                        sx={{
                            width: '100%',
                        }}
                        spacing={{
                            xs: 3,
                        }}
                    >
                        <OrderChartsWrappers data={allData.orderChartData} dateRange={dateRange} />
                    </Stack>
                </WhiteBoxContainer>
            ) : (
                <EmptyOrdersSection />
            )}
        </>
    )
}

const ChartsWrapperMemo = memo(ChartsWrapper, compareParameters)

const Dashboard = ({ scrollRef }: { scrollRef: RefObject<HTMLDivElement> }) => {
    const mixpanel = useMixpanel()
    const isTestMode = useIsTestMode()
    const defaultDateRange = {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
    }

    useTitle('Analytics')
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
    const [dateRange, setDateRange] = useState<Range | undefined>(defaultDateRange)
    const [isFilterBoxSticky, setIsFilterBoxSticky] = useState(false)
    const [accountNamesMap, setAccountNamesMap] = useState<Record<string, string>>({})
    const [analyticsType, setAnalyticsType] = useState<'Spend' | 'Logistics'>('Logistics')

    useEffect(() => {
        mixpanel.track('analytics_page_viewed')
    }, [mixpanel])

    const { recentlyUsedAccounts } = useRecentlyUsedAccounts()

    // Fetch account names for selected accounts (for chip display)
    useEffect(() => {
        if (selectedAccountIds.length) {
            getAccountsById({ accountId: selectedAccountIds }).then((accounts) => {
                const namesMap: Record<string, string> = Object.fromEntries(
                    accounts.map((acc) => [acc.id, acc.name]),
                )
                setAccountNamesMap(namesMap)
            })
        } else {
            setAccountNamesMap({})
        }
    }, [selectedAccountIds])

    useEffect(() => {
        const scrollElement = scrollRef.current
        if (!scrollElement) {
            return
        }

        const handleScroll = () => {
            setIsFilterBoxSticky(scrollElement.scrollTop >= 24)
        }

        scrollElement.addEventListener('scroll', handleScroll, { passive: true })
        handleScroll()

        return () => {
            scrollElement.removeEventListener('scroll', handleScroll)
        }
    }, [scrollRef])

    return (
        <MainLayoutContainerNew
            isTestMode={!!isTestMode}
            title={'Analytics'}
            subtitle={
                'Explore your emissions metrics, identify patterns, and track performance over time.'
            }
        >
            <FiltersWithQueryParamsNew<string>
                filters={[
                    {
                        queryParamKey: 'account_id',
                        selectedValues: selectedAccountIds,
                        setSelectedValues: (values) => setSelectedAccountIds(values),
                    },
                ]}
                setDateRange={setDateRange}
                dateRange={dateRange}
            />
            <>
                <WhiteBoxContainer
                    sx={{
                        alignItems: 'center',
                        p: 2,
                        background: `white`,
                        position: 'sticky',
                        top: isTestMode ? '114px' : '90px',
                        left: 0,
                        zIndex: 99,
                        mb: 3 / 2,
                        transition: 'margin-left .1s ease-in-out',
                        ...(isFilterBoxSticky && {
                            borderTop: '1px solid',
                            borderColor: `Surface300`,
                            width: 'calc(100% + 48px)',
                            ml: '-24px',
                            borderRadius: 0,
                        }),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <ButtonGroupNew
                            value={analyticsType}
                            items={[
                                {
                                    value: 'Logistics',
                                    label: 'Logistics',
                                },
                                {
                                    value: 'Spend',
                                    label: 'Spend',
                                },
                            ]}
                            onChange={(type) => setAnalyticsType(type as 'Spend' | 'Logistics')}
                        />
                        <FiltersBoxNew
                            preselectedDateRange={dateRange}
                            setDateRange={setDateRange}
                            preselectedAccountIds={selectedAccountIds}
                            setSelectedAccountIds={setSelectedAccountIds}
                            defaultDateRange={PredefinedRange.THIRTY_DAYS}
                            filters={[]}
                            flatFilters
                        />
                    </Box>
                    {!!selectedAccountIds.length && (
                        <Box
                            sx={{
                                ml: -3,
                                mb: -1,
                                mt: 2,
                            }}
                        >
                            <ActiveFiltersChips<string>
                                filters={[
                                    {
                                        filterName: 'account',
                                        selectedValues: selectedAccountIds,
                                        setSelectedValues: (ids) => setSelectedAccountIds(ids),
                                        transformLabel: (id) => accountNamesMap[id],
                                    },
                                ]}
                            />
                        </Box>
                    )}
                </WhiteBoxContainer>
                <WhiteBoxContainer>
                    <LoadingWrapper
                        sx={{
                            display: 'flex',
                            position: 'relative',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: `600px`,
                        }}
                        loading={!recentlyUsedAccounts}
                    >
                        <></>
                    </LoadingWrapper>
                </WhiteBoxContainer>
                {/*  Wait until recentlyUsedAccounts have been loaded to show the analytics */}
                {recentlyUsedAccounts && (
                    <ChartsWrapperMemo
                        selectedAccountIds={selectedAccountIds}
                        dateRange={dateRange ?? defaultDateRange}
                        analyticsType={analyticsType}
                    />
                )}
            </>
        </MainLayoutContainerNew>
    )
}

export default Dashboard
